package com.banyan.flightviewer.manager

import com.banyan.flightviewer.platform.SettingsStore
import com.banyan.flightviewer.state.AirportFavorite
import com.banyan.flightviewer.state.AppSettings
import com.banyan.flightviewer.state.AppState
import com.banyan.flightviewer.state.Bookmark
import io.github.oshai.kotlinlogging.KotlinLogging
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private val logger = KotlinLogging.logger {}

/**
 * Multiplatform settings manager. Persistence is delegated to [SettingsStore]
 * (Preferences on JVM, localStorage on JS).
 *
 * Desktop bootstrap may pass `envSeeds` to [loadAllSettings] -- typically the
 * parsed `.env` map. Seed values are written to the store on first load only,
 * preserving the historical behaviour where `.env` populates Preferences once
 * and is then ignored.
 */
class SettingsManager(
    private val appState: AppState,
    private val store: SettingsStore = SettingsStore(),
) {

    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Load settings into [AppState]. Optional [envSeeds] are .env-style values
     * used as a fallback for any keys that aren't already in the store; they are
     * persisted into the store the first time they're observed (matching the
     * historical Preferences-seeding behaviour).
     */
    fun loadAllSettings(envSeeds: Map<String, String> = emptyMap()) {
        val settings = AppSettings(
            apiSource = loadString("api_source", envSeeds["API_SOURCE"], "ADS-B Exchange"),
            adsbxApiKey = loadString("adsbx_api_key", envSeeds["ADSBX_API_KEY"], ""),
            fr24ApiKey = loadString("fr24_api_key", envSeeds["FR24_API_TOKEN"], ""),
            lat = loadDouble("lat", envSeeds["DEFAULT_LAT"]?.toDoubleOrNull(), 40.7128),
            lon = loadDouble("lon", envSeeds["DEFAULT_LON"]?.toDoubleOrNull(), -74.0060),
            radiusKm = loadInt("radius_km", envSeeds["DEFAULT_RADIUS_KM"]?.toIntOrNull(), 50),
            updateIntervalSec = loadInt(
                "update_interval_sec",
                envSeeds["UPDATE_INTERVAL_SEC"]?.toIntOrNull(),
                10,
            ),
            pixooEnabled = loadBoolean(
                "pixoo_enabled",
                envSeeds["PIXOO_ENABLED"]?.lowercase()?.let { it == "true" },
                false,
            ),
            pixooIpAddress = loadString("pixoo_ip_address", envSeeds["PIXOO_IP_ADDRESS"], ""),
            pixooBuzzerEnabled = loadBoolean("pixoo_buzzer_enabled", null, false),
            pixooRadiusMiles = loadDouble("pixoo_radius_miles", null, 5.0),
            pixooDisplayMode = loadString("pixoo_display_mode", null, "nearest"),
            distanceUnits = loadString("distance_units", null, "miles"),
            altitudeUnits = loadString("altitude_units", null, "ft"),
            speedUnits = loadString("speed_units", null, "kts"),
            savedAddress = loadString("address", null, ""),
            enrichmentCooldownSec = loadInt(
                "enrichment_cooldown_sec",
                envSeeds["ENRICHMENT_COOLDOWN_SEC"]?.toIntOrNull(),
                60,
            ),
            fr24BulkEnrichment = loadBoolean(
                "fr24_bulk_enrichment",
                envSeeds["FR24_BULK_ENRICHMENT"]?.lowercase()?.let { it == "true" },
                true,
            ),
        )

        appState.updateSettings(settings)
        logger.info {
            "Settings loaded: apiSource=${settings.apiSource}, lat=${settings.lat}, " +
                "lon=${settings.lon}, radius=${settings.radiusKm}km"
        }

        loadBookmarks()
        loadAirportFavorites()
    }

    private fun loadBookmarks() {
        try {
            val raw = store.getString("bookmarks") ?: return
            val bookmarks = json.decodeFromString<List<Bookmark>>(raw)
            appState.setBookmarks(bookmarks)
            logger.info { "Loaded ${bookmarks.size} bookmarks" }
        } catch (e: Exception) {
            logger.warn { "Failed to load bookmarks: ${e.message}" }
        }
    }

    fun saveBookmarks(bookmarks: List<Bookmark>) {
        try {
            store.putString("bookmarks", json.encodeToString(bookmarks))
            store.flush()
        } catch (e: Exception) {
            logger.warn { "Failed to save bookmarks: ${e.message}" }
        }
    }

    private fun loadAirportFavorites() {
        try {
            val raw = store.getString("airport_favorites") ?: return
            val favs = json.decodeFromString<List<AirportFavorite>>(raw)
            appState.setAirportFavorites(favs)
            logger.info { "Loaded ${favs.size} airport favorites" }
        } catch (e: Exception) {
            logger.warn { "Failed to load airport favorites: ${e.message}" }
        }
    }

    fun saveAirportFavorites(favorites: List<AirportFavorite>) {
        try {
            store.putString("airport_favorites", json.encodeToString(favorites))
            store.flush()
        } catch (e: Exception) {
            logger.warn { "Failed to save airport favorites: ${e.message}" }
        }
    }

    fun saveSettings(settings: AppSettings) {
        store.putString("api_source", settings.apiSource)
        store.putString("adsbx_api_key", settings.adsbxApiKey)
        store.putString("fr24_api_key", settings.fr24ApiKey)
        store.putDouble("lat", settings.lat)
        store.putDouble("lon", settings.lon)
        store.putInt("radius_km", settings.radiusKm)
        store.putInt("update_interval_sec", settings.updateIntervalSec)
        store.putBoolean("pixoo_enabled", settings.pixooEnabled)
        store.putString("pixoo_ip_address", settings.pixooIpAddress)
        store.putBoolean("pixoo_buzzer_enabled", settings.pixooBuzzerEnabled)
        store.putDouble("pixoo_radius_miles", settings.pixooRadiusMiles)
        store.putString("pixoo_display_mode", settings.pixooDisplayMode)
        store.putString("distance_units", settings.distanceUnits)
        store.putString("altitude_units", settings.altitudeUnits)
        store.putString("speed_units", settings.speedUnits)
        store.putString("address", settings.savedAddress)
        store.putInt("enrichment_cooldown_sec", settings.enrichmentCooldownSec)
        store.putBoolean("fr24_bulk_enrichment", settings.fr24BulkEnrichment)
        store.flush()

        appState.updateSettings(settings)
        logger.info { "Settings saved" }
    }

    /**
     * Load a string setting: store first, then [envValue] fallback (persisted
     * back into the store), then [default].
     */
    private fun loadString(key: String, envValue: String?, default: String): String {
        val saved = store.getString(key)
        if (!saved.isNullOrEmpty()) return saved

        if (!envValue.isNullOrEmpty()) {
            store.putString(key, envValue)
            return envValue
        }

        return default
    }

    private fun loadDouble(key: String, envValue: Double?, default: Double): Double {
        val saved = store.getDouble(key)
        if (saved != null) return saved

        if (envValue != null) {
            store.putDouble(key, envValue)
            return envValue
        }

        return default
    }

    private fun loadInt(key: String, envValue: Int?, default: Int): Int {
        val saved = store.getInt(key)
        if (saved != null) return saved

        if (envValue != null) {
            store.putInt(key, envValue)
            return envValue
        }

        return default
    }

    private fun loadBoolean(key: String, envValue: Boolean?, default: Boolean): Boolean {
        val saved = store.getBoolean(key)
        if (saved != null) return saved

        if (envValue != null) {
            store.putBoolean(key, envValue)
            return envValue
        }

        return default
    }
}
